/* ==========================================
   HEADER SEARCH OVERLAY — open/close behavior
   ========================================== */
if (!customElements.get('header-search-overlay')) {
  class HeaderSearchOverlay extends HTMLElement {
    connectedCallback() {
      this.overlay = this;
      this.input = this.querySelector('.pa-predictive-search__input');

      // Toggle buttons live elsewhere in the header
      document.querySelectorAll('[data-search-overlay-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.querySelectorAll('[data-search-overlay-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-active')) {
          this.close();
        }
      });
    }

    open() {
      this.overlay.classList.add('is-active');
      document.body.classList.add('search-overlay-open');
      document.body.style.overflow = 'hidden';
      if (this.input) {
        setTimeout(() => this.input.focus(), 200);
      }
    }

    close() {
      this.overlay.classList.remove('is-active');
      document.body.classList.remove('search-overlay-open');
      document.body.style.overflow = '';
    }
  }

  customElements.define('header-search-overlay', HeaderSearchOverlay);
}

/* ==========================================
   PA-PREDICTIVE-SEARCH — fetches /search/suggest.json
   ========================================== */
if (!customElements.get('pa-predictive-search')) {
  class PaPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.pa-predictive-search__input');
      this.results = this.querySelector('.pa-predictive-search__results');
      this.form = this.querySelector('.pa-predictive-search__form');
      this.timer = null;
      this.activeIndex = -1;
      if (!this.input) return;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', 'PaPredictiveSearchResults');
      this.results.id = 'PaPredictiveSearchResults';
      this.results.setAttribute('role', 'listbox');

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKey.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('click', (event) => {
        if (event.target.closest('.pa-predictive-search__submit')) return;
        this.input.focus();
      });
      this.input.addEventListener('focus', () => {
        if (this.results.children.length > 0 && this.input.value.trim().length > 1) this.show();
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hide();
      });
    }

    onSubmit(event) {
      const q = this.input.value.trim();
      if (!q) {
        event.preventDefault();
        this.input.focus();
      }
    }

    onInput() {
      clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.hide();
        return;
      }
      this.results.innerHTML = '<div class="pa-predictive-search__empty">' + (this.dataset.loadingText || 'Buscando...') + '</div>';
      this.show();
      this.timer = setTimeout(() => this.fetch(q), 220);
    }

    onKey(e) {
      const items = this.results.querySelectorAll('.pa-predictive-search__item');
      if (e.key === 'Escape') {
        this.hide();
        this.input.blur();
        return;
      }
      if (!items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = Math.min(items.length - 1, this.activeIndex + 1);
        this.highlight(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = Math.max(-1, this.activeIndex - 1);
        this.highlight(items);
      } else if (e.key === 'Enter' && this.activeIndex >= 0) {
        e.preventDefault();
        items[this.activeIndex].click();
      }
    }

    highlight(items) {
      items.forEach((el, i) => el.classList.toggle('is-active', i === this.activeIndex));
      if (this.activeIndex >= 0) items[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }

    fetch(q) {
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page,article&resources[limit]=8';
      fetch(url)
        .then((r) => r.json())
        .then((data) => this.render(data, q))
        .catch(() => {
          this.results.innerHTML = '<div class="pa-predictive-search__empty">Búsqueda no disponible.</div>';
        });
    }

    render(data, q) {
      const r = data && data.resources && data.resources.results;
      if (!r) {
        this.hide();
        return;
      }
      const products = r.products || [];
      const collections = r.collections || [];
      const pages = r.pages || [];
      const articles = r.articles || [];
      const safeQ = q.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const labels = {
        products: this.dataset.labelProducts || 'Productos',
        collections: this.dataset.labelCollections || 'Colecciones',
        content: this.dataset.labelContent || 'Contenido',
        seeAll: this.dataset.labelSeeAll || 'Ver todo para',
        empty: this.dataset.labelEmpty || 'No hay resultados para',
      };
      let html = '';

      if (products.length) {
        html += '<div class="pa-predictive-search__group"><div class="pa-predictive-search__group-title">' + labels.products + '</div>';
        html += products.map((p) => `
          <a class="pa-predictive-search__item" role="option" href="${p.url}">
            ${p.image ? `<img src="${p.image}" alt="${(p.title || '').replace(/"/g, '&quot;')}" loading="lazy">` : ''}
            <span><span class="pa-predictive-search__item-title">${p.title}</span>
            <span class="pa-predictive-search__item-price">${p.price || ''}</span></span>
          </a>`).join('');
        html += '</div>';
      }

      if (collections.length) {
        html += '<div class="pa-predictive-search__group"><div class="pa-predictive-search__group-title">' + labels.collections + '</div>';
        html += collections.map((c) => `<a class="pa-predictive-search__item" role="option" href="${c.url}"><span class="pa-predictive-search__item-title">${c.title}</span></a>`).join('');
        html += '</div>';
      }

      if (pages.length || articles.length) {
        html += '<div class="pa-predictive-search__group"><div class="pa-predictive-search__group-title">' + labels.content + '</div>';
        html += pages.map((p) => `<a class="pa-predictive-search__item" role="option" href="${p.url}"><span class="pa-predictive-search__item-title">${p.title}</span></a>`).join('');
        html += articles.map((a) => `<a class="pa-predictive-search__item" role="option" href="${a.url}"><span class="pa-predictive-search__item-title">${a.title}</span></a>`).join('');
        html += '</div>';
      }

      if (html) {
        html += `<a class="pa-predictive-search__item pa-predictive-search__item--all" role="option" href="/search?q=${encodeURIComponent(q)}&type=product"><span>${labels.seeAll} "${safeQ}"</span><span aria-hidden="true">+</span></a>`;
      } else {
        html = `<div class="pa-predictive-search__empty">${labels.empty} "${safeQ}".</div>`;
      }
      this.results.innerHTML = html;
      this.activeIndex = -1;
      this.show();
    }

    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }
  }

  customElements.define('pa-predictive-search', PaPredictiveSearch);
}
